import { Overlay } from '@hermes/ui';
import { GetVssStatusResDto, VssStatusStatus } from '@portal-middleware/hooks';
import { Query, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, Favicon } from '@vestwell-frontend/elements';
import {
  getBearerToken,
  getMobileHostname,
  initializeSplashScreen,
  isNativeMobile,
  logout,
  setMobileHeaders
} from '@vestwell-frontend/helpers';
import { ApiBoundary, ThemeProvider } from '@vestwell-frontend/ui';

import axios, { AxiosError } from 'axios';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { AppRouter } from './router';
import { routes } from './routes';

(async () => {
  const isNative = await isNativeMobile();
  let mobileHost;
  if (isNative) {
    await setMobileHeaders(axios);
    mobileHost = await getMobileHostname();
  }
  const token = await getBearerToken(isNative);

  axios.defaults.headers.common['Authorization'] = token;

  const unauthorizedLanding = isNative
    ? `${window.location.protocol}//${mobileHost}?logout=true`
    : window.location.origin;

  const redirectIfInMaintenance = (data: GetVssStatusResDto) => {
    if (
      data?.metadata?.status &&
      data?.metadata?.status?.status !== VssStatusStatus.normal &&
      window.location.href !==
        window.location.origin + routes.GiftingMaintenancePage
    ) {
      window.location.href =
        window.location.origin + routes.GiftingMaintenancePage;
    }
  };

  const queryClient = new QueryClient({
    defaultOptions: {
      mutations: {
        onError: async (e: AxiosError) => {
          if (e?.status === 401 || e?.message === 'Unauthorized') {
            await logout();
            window.location.href = unauthorizedLanding;
          }
        },
        onSettled: (data: GetVssStatusResDto) => {
          redirectIfInMaintenance(data);
        },
        retry: false,
        useErrorBoundary: false
      },
      queries: {
        cacheTime: 60 * 1000 * 5,
        onError: async (e: AxiosError) => {
          if (
            (e?.status === 401 || e?.message === 'Unauthorized') &&
            window.location.href.includes('/gifting/account')
          ) {
            await logout();
            window.location.href = unauthorizedLanding;
          }
        },
        onSettled: (data: GetVssStatusResDto) => {
          redirectIfInMaintenance(data);
        },
        refetchOnMount: false,
        refetchOnReconnect: 'always',
        refetchOnWindowFocus: false,
        retry: (failureCount, error: AxiosError) =>
          (['ECONNABORTED', 'ENOTFOUND', 'ETIMEDOUT', 'ECONNREFUSED'].includes(
            error?.code
          ) ||
            [503, 504].includes(error?.status)) &&
          failureCount < 6,
        suspense: false,
        useErrorBoundary(error: AxiosError, query: Query) {
          if (
            error?.status === 400 ||
            error?.status === 404 ||
            query.options.queryHash.includes('/auth')
          ) {
            return false;
          }
          return true;
        }
      }
    }
  });

  await initializeSplashScreen();

  const root = createRoot(document.getElementById('root'));

  root.render(
    <AuthProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <Favicon />
            <Overlay />
            <ApiBoundary>
              <AppRouter />
            </ApiBoundary>
          </ThemeProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </AuthProvider>
  );
})();
